import { forwardRef, Children, isValidElement, ReactNode, PropsWithChildren } from "react";
import { RefComponent } from "capitalrx-components-2.0/lib/forms";
import { AccessibilityProps } from "capitalrx-components-2.0/lib/AccessibilityProps";
import { BaseProps } from "capitalrx-components-2.0/lib/BaseProps";
import { getHtmlPropsFromBaseProps } from "../../utilities/getHtmlPropsFromBaseProps";
import { withSubComponents } from "../../utilities/withSubComponents";
import classNames from "classnames";
import { Spacer } from "./Spacer";

import tokenStyles from "./styles.module.css";

import { Spacing } from "../../layout";
import { StackAlignment } from "./StackAlignment";
import { createComponentStyleHook } from "../../tokens";

/** The stack component properties. */
export interface StackProps extends BaseProps, AccessibilityProps {

  /** Specifies the horizontal alignment of the elements within the stack. If not specified defaults to `"span"`. */
  readonly alignment?: StackAlignment

  /** When `true`, the stack fills the available height of its parent. */
  readonly fullHeight?: boolean;

  /** Indicates whether the stack should render children in reverse order. If not specified, defaults to `false` */
  readonly reverse?: boolean;

  /** The spacing between items. If not specified defaults to "medium". */
  readonly spacing?: Spacing;

  /** The component children. */
  readonly children?: ReactNode;
}

/** The `Stack` component subcomponents. */
export interface StackSubComponents {

  /** A component which pushes content in a stack to the top and bottom. */
  readonly Spacer: typeof Spacer;
}

const useResolvedStackStyles = createComponentStyleHook<PropsWithChildren<StackProps>>({
  legacyStyles: () => import("./legacy/styles.module.scss"),
  tokenStyles,
  componentTokens: "stack",
});

/** Stacks child elements on top of each other. */
export const Stack = withSubComponents<RefComponent<HTMLDivElement, StackProps>, StackSubComponents>(
  {
    Spacer,
  },
  forwardRef<HTMLDivElement, StackProps>((props, ref) => {
    const { mode, styles, StyleModeBoundary } = useResolvedStackStyles(props);
    const { alignment = "span", fullHeight = false, spacing = "medium", reverse = false, children, role } = props;
    const baseHtmlProps = getHtmlPropsFromBaseProps(props, {
      className: [styles.Stack, "stack", `align-${alignment}`, `space-${spacing}`, { fullHeight, reverse }],
      testIdPrefix: "stack",
    });

    return (
      <StyleModeBoundary mode={mode}>
        <div
          {...baseHtmlProps}
          ref={ref}
          role={role}
          aria-busy={props["aria-busy"]}
          aria-label={props["aria-label"]}
          aria-labelledby={props["aria-labelledby"]}
          aria-posinset={props["aria-posinset"]}
          aria-setsize={props["aria-setsize"]}
        >
          {Children.map(children, (child) => {
            if (!isValidElement(child) || child.type !== Spacer) return child;

            return <div className={classNames(styles.Spacer, "spacer")} />;
          })}
        </div>
      </StyleModeBoundary>
    );
  },
  ));
